/**
 * Google Analytics 4 integration for DPEWizard
 * Tracking user behavior, page views, and custom events
 */

// Types for analytics events
export type EventCategory = 
  | 'page_view'
  | 'user'
  | 'analysis'
  | 'payment'
  | 'subscription'
  | 'error'
  | 'performance'
  | 'engagement'
  | 'navigation'
  | 'form';

export type EventAction =
  | 'view'
  | 'click'
  | 'submit'
  | 'start'
  | 'complete'
  | 'error'
  | 'purchase'
  | 'subscribe'
  | 'cancel'
  | 'login'
  | 'logout'
  | 'register'
  | 'scroll'
  | 'download'
  | 'share'
  | 'search';

export interface AnalyticsEvent {
  action: EventAction;
  category: EventCategory;
  label?: string;
  value?: number;
  custom_parameters?: Record<string, any>;
}

export interface PageViewEvent {
  page_title: string;
  page_location: string;
  page_path: string;
  referrer?: string;
}

// Google Analytics 4 configuration
const GA4_MEASUREMENT_ID = 'G-JP99R58XZ2';

// Main analytics class
export class GoogleAnalytics {
  private static instance: GoogleAnalytics;
  private initialized: boolean = false;
  private userId: string | null = null;
  private debugMode: boolean = false;
  private consentGiven: boolean = true; // Default to true, can be changed based on cookie consent

  private constructor() {}

  static getInstance(): GoogleAnalytics {
    if (!GoogleAnalytics.instance) {
      GoogleAnalytics.instance = new GoogleAnalytics();
    }
    return GoogleAnalytics.instance;
  }

  /**
   * Initialize Google Analytics 4
   */
  init(options: { debugMode?: boolean; consentGiven?: boolean } = {}): void {
    if (this.initialized) return;
    
    this.debugMode = options.debugMode || process.env.NODE_ENV === 'development';
    this.consentGiven = options.consentGiven ?? true;
    
    if (!this.consentGiven) {
      console.log('[GA4] Analytics disabled - no consent given');
      return;
    }

    try {
      // Load gtag script
      this.loadGtagScript();
      
      // Configure GA4
      this.configureGA4();
      
      this.initialized = true;
      
      if (this.debugMode) {
        console.log('[GA4] Google Analytics 4 initialized with ID:', GA4_MEASUREMENT_ID);
      }
    } catch (error) {
      console.error('[GA4] Failed to initialize Google Analytics:', error);
    }
  }

  /**
   * Load Google Analytics script
   */
  private loadGtagScript(): void {
    // Check if script already exists
    if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}"]`)) {
      return;
    }

    // Create and load gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Initialize gtag function
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() {
      window.dataLayer.push(arguments);
    };
  }

  /**
   * Configure Google Analytics 4
   */
  private configureGA4(): void {
    if (typeof window.gtag !== 'function') {
      console.error('[GA4] gtag function not available');
      return;
    }

    // Initialize gtag
    window.gtag('js', new Date());
    
    // Configure GA4 with privacy settings
    window.gtag('config', GA4_MEASUREMENT_ID, {
      // Privacy settings
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      
      // Performance settings
      send_page_view: false, // We'll send page views manually
      
      // Debug mode
      debug_mode: this.debugMode,
      
      // Custom parameters
      custom_map: {
        'user_type': 'user_type',
        'subscription_status': 'subscription_status'
      }
    });

    if (this.debugMode) {
      console.log('[GA4] GA4 configured with privacy settings');
    }
  }

  /**
   * Set user ID for tracking
   */
  setUserId(userId: string): void {
    if (!this.initialized || !this.consentGiven) return;
    
    this.userId = userId;
    
    if (typeof window.gtag === 'function') {
      window.gtag('config', GA4_MEASUREMENT_ID, {
        user_id: userId
      });
      
      if (this.debugMode) {
        console.log('[GA4] User ID set:', userId);
      }
    }
  }

  /**
   * Clear user ID
   */
  clearUserId(): void {
    if (!this.initialized || !this.consentGiven) return;
    
    this.userId = null;
    
    if (typeof window.gtag === 'function') {
      window.gtag('config', GA4_MEASUREMENT_ID, {
        user_id: null
      });
      
      if (this.debugMode) {
        console.log('[GA4] User ID cleared');
      }
    }
  }

  /**
   * Track page view
   */
  trackPageView(data: PageViewEvent): void {
    if (!this.initialized || !this.consentGiven) return;
    
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: data.page_title,
        page_location: data.page_location,
        page_path: data.page_path,
        referrer: data.referrer || document.referrer,
        user_id: this.userId
      });
      
      if (this.debugMode) {
        console.log('[GA4] Page view tracked:', {
          page_title: data.page_title,
          page_location: data.page_location,
          page_path: data.page_path,
          user_id: this.userId
        });
      }
    }
  }

  /**
   * Track custom event
   */
  trackEvent(event: AnalyticsEvent): void {
    if (!this.initialized || !this.consentGiven) return;
    
    if (typeof window.gtag === 'function') {
      const eventData = {
        event_category: event.category,
        event_label: event.label,
        value: event.value,
        user_id: this.userId,
        ...event.custom_parameters
      };
      
      window.gtag('event', event.action, eventData);
      
      if (this.debugMode) {
        console.log('[GA4] Event tracked:', event.action, eventData);
      }
    }
  }

  /**
   * Track user login
   */
  trackLogin(userId: string, method: string): void {
    this.setUserId(userId);
    
    this.trackEvent({
      action: 'login',
      category: 'user',
      label: method,
      custom_parameters: {
        method: method
      }
    });
  }

  /**
   * Track user registration
   */
  trackRegistration(userId: string): void {
    this.setUserId(userId);
    
    this.trackEvent({
      action: 'register',
      category: 'user',
      custom_parameters: {
        method: 'email'
      }
    });
  }

  /**
   * Track user logout
   */
  trackLogout(): void {
    this.trackEvent({
      action: 'logout',
      category: 'user'
    });
    
    this.clearUserId();
  }

  /**
   * Track DPE analysis events
   */
  trackAnalysisStart(analysisId: string, fileSize?: number): void {
    this.trackEvent({
      action: 'start',
      category: 'analysis',
      label: analysisId,
      value: fileSize,
      custom_parameters: {
        analysis_id: analysisId,
        file_size: fileSize
      }
    });
  }

  trackAnalysisComplete(analysisId: string, duration: number, success: boolean): void {
    this.trackEvent({
      action: 'complete',
      category: 'analysis',
      label: analysisId,
      value: duration,
      custom_parameters: {
        analysis_id: analysisId,
        duration_ms: duration,
        success: success
      }
    });
  }

  /**
   * Track payment events
   */
  trackPayment(amount: number, currency: string, productId: string): void {
    if (typeof window.gtag === 'function') {
      // Use GA4 enhanced ecommerce event
      window.gtag('event', 'purchase', {
        transaction_id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        value: amount,
        currency: currency,
        items: [{
          item_id: productId,
          item_name: productId === 'monthly_unlimited' ? 'Abonnement Mensuel' : 'Crédits DPE',
          category: productId === 'monthly_unlimited' ? 'subscription' : 'credits',
          quantity: 1,
          price: amount
        }]
      });
    }
  }

  /**
   * Track subscription events
   */
  trackSubscription(planId: string, amount: number, currency: string): void {
    this.trackEvent({
      action: 'subscribe',
      category: 'subscription',
      label: planId,
      value: amount,
      custom_parameters: {
        plan_id: planId,
        currency: currency
      }
    });
  }

  /**
   * Track form interactions
   */
  trackFormStart(formName: string): void {
    this.trackEvent({
      action: 'start',
      category: 'form',
      label: formName
    });
  }

  trackFormSubmit(formName: string, success: boolean): void {
    this.trackEvent({
      action: 'submit',
      category: 'form',
      label: formName,
      custom_parameters: {
        success: success
      }
    });
  }

  /**
   * Track navigation events
   */
  trackNavigation(from: string, to: string): void {
    this.trackEvent({
      action: 'click',
      category: 'navigation',
      label: `${from} -> ${to}`,
      custom_parameters: {
        from_page: from,
        to_page: to
      }
    });
  }

  /**
   * Track scroll depth
   */
  trackScrollDepth(depth: number): void {
    this.trackEvent({
      action: 'scroll',
      category: 'engagement',
      label: `${depth}%`,
      value: depth
    });
  }

  /**
   * Track file downloads
   */
  trackDownload(fileName: string, fileType: string): void {
    this.trackEvent({
      action: 'download',
      category: 'engagement',
      label: fileName,
      custom_parameters: {
        file_name: fileName,
        file_type: fileType
      }
    });
  }

  /**
   * Track external link clicks
   */
  trackExternalLink(url: string): void {
    this.trackEvent({
      action: 'click',
      category: 'navigation',
      label: 'external_link',
      custom_parameters: {
        link_url: url,
        link_domain: new URL(url).hostname
      }
    });
  }

  /**
   * Track errors
   */
  trackError(error: Error, context?: string): void {
    this.trackEvent({
      action: 'error',
      category: 'error',
      label: error.name,
      custom_parameters: {
        error_message: error.message,
        error_stack: error.stack?.substring(0, 500), // Limit stack trace length
        context: context
      }
    });
  }

  /**
   * Track search events
   */
  trackSearch(searchTerm: string, resultsCount?: number): void {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'search', {
        search_term: searchTerm,
        results_count: resultsCount
      });
    }
  }

  /**
   * Update consent status
   */
  updateConsent(consentGiven: boolean): void {
    this.consentGiven = consentGiven;
    
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: consentGiven ? 'granted' : 'denied',
        ad_storage: 'denied' // We don't use ads
      });
    }
    
    if (!consentGiven && this.initialized) {
      // Disable tracking
      this.clearUserId();
    } else if (consentGiven && !this.initialized) {
      // Re-initialize if consent is given
      this.init({ consentGiven: true, debugMode: this.debugMode });
    }
  }
}

// Export singleton instance
export const analytics = GoogleAnalytics.getInstance();

// Utility functions for common tracking scenarios
export const trackButtonClick = (buttonName: string, location: string) => {
  analytics.trackEvent({
    action: 'click',
    category: 'engagement',
    label: buttonName,
    custom_parameters: {
      button_name: buttonName,
      page_location: location
    }
  });
};

export const trackFormInteraction = (formName: string, action: 'start' | 'submit' | 'error', details?: any) => {
  analytics.trackEvent({
    action: action,
    category: 'form',
    label: formName,
    custom_parameters: {
      form_name: formName,
      ...details
    }
  });
};

export const trackTimeOnPage = (pageName: string, timeSpent: number) => {
  analytics.trackEvent({
    action: 'view',
    category: 'engagement',
    label: 'time_on_page',
    value: timeSpent,
    custom_parameters: {
      page_name: pageName,
      time_spent_seconds: timeSpent
    }
  });
};

// Global type declarations
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export default analytics;